import { Pressable, View } from "react-native";
import { Checkbox, RadioButton, Text } from "react-native-paper";
import { Control, Controller } from "react-hook-form";
import moment from "moment";
import DateInput from "./DateInput";
import DropDown from "../../components/Dropdown";
import HTMLDisplay from "../../components/HTMLDisplay";
import { EventDetailsQuery, FormData } from "./types";
import { colors } from "../../../theme";
import { DateOfBirthInfoButton } from "./modals/DateOfBirthModal";

export const DateOfBirthField = ({
  control,
  hasExistingDateOfBirth,
  setDateOfBirthModalVisible,
}: {
  control: Control<FormData>;
  hasExistingDateOfBirth: boolean;
  setDateOfBirthModalVisible: (visible: boolean) => void;
}) => {
  return (
    <View className="relative">
      <Controller
        name="dateOfBirth"
        control={control}
        rules={{
          required: "Date of Birth is required",
          validate: {
            validDate: (value) =>
              moment(value, "MM/DD/YYYY", true).isValid() || "Invalid date, please enter date of birth in MM/DD/YYYY format",
            isFutureDate: (value) =>
              !(moment(value, "MM/DD/YYYY", true) > moment()) || "Invalid, date of birth cannot be a future date",
            minimumAgeRequirement: (value) =>
              moment().diff(moment(value, "MM/DD/YYYY", true), "years") >= 18 || "Invalid, must be at least 18 years old",
          },
        }}
        render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
          <View className="relative">
            <DateInput
              name="dateOfBirth"
              label="Date of Birth"
              onChange={onChange}
              disabled={hasExistingDateOfBirth}
              value={value as string}
              error={error}
              onBlur={onBlur}
            />
            {hasExistingDateOfBirth && <DateOfBirthInfoButton onPress={() => setDateOfBirthModalVisible(true)} />}
          </View>
        )}
      />
    </View>
  );
};

export const GenderField = ({ control }: { control: Control<FormData> }) => (
  <View className="mt-4">
    <Controller
      name="gender"
      control={control}
      rules={{
        required: "Gender is required",
      }}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <DropDown
          label="Gender"
          setValue={onChange}
          value={value ?? ""}
          list={["Male", "Female"].map((label: string) => ({ label, value: label }))}
          error={error}
          testID="gender-select"
        />
      )}
    />
  </View>
);

export const AdditionalGuestsComponent = ({
  control,
  event,
}: {
  control: Control<FormData>;
  event: EventDetailsQuery["event"];
}) => {
  return (
    <Controller
      name="showAdditionalGuests"
      control={control}
      render={({ field: { onChange, value } }) => (
        <View>
          <View className="bg-sky-light h-[1px] my-8" />
          <Text className="text-large-normal font-700">Additional Guests</Text>
          <Text className="text-regular-normal mt-2">Will you be attending with guest(s)?</Text>
          <RadioButton.Group onValueChange={(newValue) => onChange(newValue === "true")} value={value ? "true" : "false"}>
            <View className="flex flex-row mt-3">
              <View className="flex flex-row items-center">
                <RadioButton.Android
                  value="true"
                  status={value ? "checked" : "unchecked"}
                  uncheckedColor={colors.sky.DEFAULT}
                  testID="additional-guests-yes-btn"
                />
                <Text className="font-400 text-small-normal text-black mr-6">Yes</Text>
              </View>

              <View className="flex flex-row items-center">
                <RadioButton.Android
                  value="false"
                  status={!value ? "checked" : "unchecked"}
                  uncheckedColor={colors.sky.DEFAULT}
                  testID="additional-guests-no-btn"
                />
                <Text className="font-400 text-small-normal text-black">No</Text>
              </View>
            </View>
          </RadioButton.Group>

          {value && <AdditionalGuestsField control={control} event={event} />}
        </View>
      )}
    />
  );
};

export const AdditionalGuestsField = ({
  control,
  event,
}: {
  control: Control<FormData>;
  event: EventDetailsQuery["event"];
}) => {
  const additionalGuestsCopy =
    event?.slug === "2025-ryder-cup-pre-registration"
      ? "PGA of America Members and their accompanying spouse/partner or guest (limit 1) may receive complimentary PGA Member Club tickets. Members' immediate children, under 21 years of age, will also receive complimentary access."
      : "PGA of America Members, Associates and PGM University Students, their accompanying spouse/partner OR guest (limit 1), immediate children under 21 years of age, parents and parents-in-law will receive complimentary access.";

  return (
    <View>
      <Controller
        name="additionalGuests"
        control={control}
        shouldUnregister={true}
        rules={{
          required: "Number of guests is required",
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <View>
            <View className="w-full">
              <Text className="text-regular-normal mt-6 mb-4">
                Select the number of guest(s). Up to 10 guests are permitted.
              </Text>
              <View>
                <DropDown
                  setValue={onChange}
                  label="Select Guests (Maximum of 10)"
                  error={error}
                  value={value ?? ""}
                  list={[
                    { label: "1 Guest", value: 1 },
                    { label: "2 Guests", value: 2 },
                    { label: "3 Guests", value: 3 },
                    { label: "4 Guests", value: 4 },
                    { label: "5 Guests", value: 5 },
                    { label: "6 Guests", value: 6 },
                    { label: "7 Guests", value: 7 },
                    { label: "8 Guests", value: 8 },
                    { label: "9 Guests", value: 9 },
                    { label: "10 Guests", value: 10 },
                  ]}
                  testID="additional-guests-select"
                />
              </View>
            </View>
            <Text className="text-regular-normal mt-6">{additionalGuestsCopy}</Text>
          </View>
        )}
      />
    </View>
  );
};

const AgreementCheckbox = ({
  onChange,
  value,
  event,
}: {
  onChange: (value: boolean) => void;
  value: boolean;
  event: EventDetailsQuery["event"];
}) => {
  const content = event?.termsContent || "<p>I have read and agree to the PGA of America Terms and Privacy Policy</p>";
  return (
    <Pressable
      className="flex flex-row items-start"
      testID="agreement-checkbox"
      onPress={() => {
        onChange(!value);
      }}
    >
      <View className="self-start mt-2">
        <Checkbox.Android status={value ? "checked" : "unchecked"} />
      </View>
      <View className="flex-1 ml-2">
        <HTMLDisplay html={content} fontSize={16} lineHeight={24} color={colors.ink.dark} />
      </View>
    </Pressable>
  );
};

export const DisclaimerField = ({ control, event }: { control: Control<FormData>; event: EventDetailsQuery["event"] }) => (
  <View>
    <View className="bg-sky-light h-[1px] my-8" />
    <View>
      <Controller
        name="disclaimer"
        control={control}
        render={({ field: { onChange, value } }) => <AgreementCheckbox {...{ onChange, value, event }} />}
      />
    </View>
  </View>
);
